
import Vue from 'vue';
import { CvButton } from '@carbon/vue';
import HeatMap16 from '@carbon/icons-vue/es/heat-map/16';
import {
  revalidateSmallDetails,
  SmallDetailsLoader,
  SmallDetailsMultipleLogos,
} from '@/components/SmallDetails';
import SmallDetailsConfirmation from '@/components/SmallDetails/Confirmation/SmallDetailsConfirmation.vue';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useNotificationStore } from '@/stores/notification';
import { UpdatePersonalizationCommand } from '@/web-components/import-wizard/typings/common/createPersonalization';
import {
  CreatePersonalizationForm,
  Personalization,
} from '@midocean/webapps-shared-domain';
import {
  getArtworkFiles,
  getProofFormFromState,
  getSmallDetailsFiles,
  getVariantFiles,
  getTextFiles,
} from '@/utils/personalizationHelper';
import { useStore as useLangStore } from '@/stores/lang';
import customerService from '@/services/CustomersService';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { PipelineNotificationType } from '@/components/Notifiers/typings/PipelineNotificationType';
import { PdpChoicesSetterType } from '@/setters/typings';
import { getIntershopRedirectionUrl } from '@/utils/index';
import { mapState } from 'pinia';

declare module 'vue/types/vue' {
  interface Vue {
    $pdpChoices: PdpChoicesSetterType;
    $sapCustomerId: string;
    $isEditMode: boolean;
    $personalization: Personalization;
    $isDirectApproval: boolean;
    $isConceptDisabled: boolean;
    $isOrderDisabled: boolean;
    $customerCompanyLogoUrl: string;
    $context: string;
    $userLoginOptions: { data: string; iv: string };
  }
}

export default Vue.extend({
  name: 'ManualCheck',
  components: {
    SmallDetailsLoader,
    SmallDetailsMultipleLogos,
    SmallDetailsConfirmation,
    CvButton,
    HeatMap16,
  },
  inject: [
    '$pdpChoices',
    '$sapCustomerId',
    '$isEditMode',
    '$personalization',
    '$isDirectApproval',
    '$isConceptDisabled',
    '$isOrderDisabled',
    '$customerCompanyLogoUrl',
    '$context',
    '$userLoginOptions',
  ],
  props: {
    triggerSmallDetailsCheck: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConcept: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isLoaderOpen: false,
      isSmallDetailsOpen: false,
      isConfirmationOpen: false,
    };
  },
  setup() {
    const { shapes, positionShapeMap } = useShapeStore();
    const { data, unavailablePrintPositions } = useProductStore();
    const { languageInUse } = useLangStore();
    const { notificationsForBell, updateSmallDetailsNotifications } =
      useNotificationStore();

    return {
      shapes,
      positionShapeMap,
      data,
      unavailablePrintPositions,
      languageInUse,
      // notificationsForBell,
      updateSmallDetailsNotifications,
      getIntershopRedirectionUrl,
    };
  },
  computed: {
    isDisabled(): boolean {
      return (
        this.shapes.filter((x) => x.smallDetailsResult !== undefined).length ===
        0
      );
    },
    checkStatusLabel(): string {
      const noCheckShapes = this.shapes.filter(
        (x) =>
          x.smallDetailsResult &&
          x.smallDetailsResult.status === 'NoCheck' &&
          x.smallDetailsResult.isCheckValid
      );
      if (
        this.shapes.length === 0 ||
        noCheckShapes.length === this.shapes.length
      )
        return this.$t('small-details.description.no-check').toString();

      const newCheckNeeded =
        this.shapes.filter(
          (x) =>
            x.smallDetailsResult && x.smallDetailsResult.isCheckValid === false
        ).length > 0;

      return newCheckNeeded
        ? this.$t('small-details.description.invalid-check').toString()
        : this.$t('small-details.description.valid-check').toString();
    },
  },
  watch: {
    triggerSmallDetailsCheck(newValue) {
      if (newValue) {
        this.onManualCheck();
      }
    },
  },
  methods: {
    async onManualCheck() {
      this.$modal.hide('pdf-preview-modal');

      const isRevalidationNeeded =
        this.shapes.filter(
          (x) =>
            x.smallDetailsResult && x.smallDetailsResult.isCheckValid === false
        ).length > 0;

      if (isRevalidationNeeded) {
        this.isLoaderOpen = true;
        await revalidateSmallDetails();

        this.isLoaderOpen = false;
        this.updateSmallDetailsNotifications();
      }

      const isConfirmationNeeded =
        this.shapes.filter((x) => x.smallDetailsResult?.status !== 'Good')
          .length > 0;

      if (this.triggerSmallDetailsCheck && isConfirmationNeeded) {
        this.isConfirmationOpen = true;
      } else if (this.triggerSmallDetailsCheck && !isConfirmationNeeded) {
        this.trySavePersonalization();
      } else {
        this.isSmallDetailsOpen = true;
      }
    },

    onUseCurrentDesign() {
      const unAcceptedShapes = this.shapes.filter(
        (x) => x.smallDetailsResult?.status !== 'Good'
      );

      const utcDate = new Date().toISOString();

      unAcceptedShapes.map((x) => {
        if (x.smallDetailsResult) {
          x.smallDetailsResult.approvedAt = utcDate;
        }
      });

      this.trySavePersonalization();

      this.isConfirmationOpen = false;
    },
    async trySavePersonalization() {
      const { sku, name } = this.data;

      const files = await getArtworkFiles(this.shapes);
      const variantFiles = getVariantFiles(this.shapes);
      const smallDetailsFiles = DoSmallDetailsPart
        ? getSmallDetailsFiles(this.shapes)
        : [];
      const textFiles = getTextFiles(this.shapes);

      const notificationStore = useNotificationStore();
      const shapeStore = useShapeStore();

      if (!this.$isEditMode) {
        try {
          const { productVariants: pdpChoicesProductVariants } =
            this.$pdpChoices;

          const form: CreatePersonalizationForm = {
            externalCustomerId: this.$sapCustomerId,
            productSKU: sku,
            productName: name,
            isConcept: this.isConcept,
            isDirectApproval: this.$isDirectApproval,
            createdFromMocca: shapeStore.isMoccaPersonalization,
            customerCompanyLogoUrl: this.$customerCompanyLogoUrl,
            contactLanguageCode: this.languageInUse,
            creator: '',
            proofType: 'AdvancedPersonalization',
            productVariants: pdpChoicesProductVariants,
            newProofForm: getProofFormFromState(false),
            remarkCodes: notificationStore.notificationsForBell
              .filter(
                (notification: PipelineNotificationType) =>
                  notification.validatorName === 'commercialRemark'
              )
              .map((notification) => notification.message),
          } as unknown as CreatePersonalizationForm;

          const {
            data: { id: personalizationId },
          } = await customerService.submitPersonalization(
            files,
            variantFiles,
            smallDetailsFiles,
            textFiles,
            form
          );

          this.sendEvent({ personalizationId });
          this.$emit('action:disable-pintura');

          if (this.$context === 'CustomerCare') {
            const url = this.getIntershopRedirectionUrl(
              personalizationId,
              this.isConcept,
              this.$userLoginOptions.data,
              this.$userLoginOptions.iv
            );
            window.open(url, '_blank');
          }
        } catch (e) {
          console.log(e);
        }
      }

      if (this.$isEditMode) {
        const command: UpdatePersonalizationCommand = {
          name: this.$personalization.proofs[0].name ?? '',
          remarkCodes: notificationStore.notificationsForBell
            .filter(
              (notification: PipelineNotificationType) =>
                notification.validatorName === 'commercialRemark'
            )
            .map((notification) => notification.message),
          ...getProofFormFromState(false),
        };
        await customerService.updatePersonalization(
          files,
          variantFiles,
          smallDetailsFiles,
          textFiles,
          command,
          this.$personalization.id,
          this.$personalization.proofs[0].id
        );

        //Check if Order this item btn is clicked, if yes - complete personalization
        if (!this.isConcept) {
          await customerService.completePersonalization(
            this.$personalization.id
          );
        }

        this.sendEvent({ personalizationId: this.$personalization.id });
        this.$emit('action:disable-pintura');
      }
    },
    sendEvent(payload: { personalizationId: string }) {
      if (!this.$root.$refs.inner) return;
      (this.$root.$refs.inner as Vue).$emit(
        this.isConcept ? 'wizard:saved-as-concept' : 'wizard:ordered',
        payload
      );
    },
  },
});
